import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, Clock, Archive, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api, mediaUrl, errMsg } from "@/lib/galleryApi";
import { fmtDate, timeLeft } from "@/components/gallery/retentionUi";

export function RetentionBanner({ g }) {
  const r = g.retention;
  if (!r || r.retention_disabled) return null;
  const done = r.keep_status === "confirmed";
  const msg = {
    active: done ? `Vos ${r.keep_count} photographies sont confirmees. Galerie complete disponible jusqu'au ${fmtDate(r.available_until)}.`
      : `Votre galerie complete expire dans ${timeLeft(r.seconds_left)} (le ${fmtDate(r.available_until)}). Pensez a telecharger vos photos et a selectionner les ${r.keep_count} photographies a conserver.`,
    grace: `La periode de telechargement est terminee. Choisissez vos ${r.keep_count} photos avant le ${fmtDate(r.grace_until)} ou contactez GLOTECH EVENT pour prolonger l'acces.`,
    kept: `Galerie archivee : vos ${r.keep_count} photographies conservees restent consultables.`,
    expired: "Cette galerie a expire sans selection. Contactez GLOTECH EVENT pour retrouver votre reportage.",
    open: done ? null : `Choisissez des maintenant les ${r.keep_count} photographies qui resteront visibles apres la periode de telechargement.`,
  }[r.phase];
  if (!msg) return null;
  const urgent = r.phase === "active" && !done && r.seconds_left < 3 * 86400;
  const Icon = r.phase === "kept" ? Archive : ["grace", "expired"].includes(r.phase) || urgent ? AlertTriangle : done ? CheckCircle2 : Clock;
  const tone = ["grace", "expired"].includes(r.phase) || urgent ? "border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#FCD34D]" : "border-[#0099CC]/30 bg-[#0099CC]/10 text-[#7DD3FC]";
  return <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 mb-6 text-sm ${tone}`} data-testid="client-retention-banner"><Icon className="w-4 h-4 shrink-0 mt-0.5" /><span>{msg}</span></div>;
}

export function KeepConfirmDialog({ open, onOpenChange, galleryId, photos, ids, required, onRemove, onConfirmed }) {
  const [consent, setConsent] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setConsent(null); }, [open]);
  const chosen = ids.map((id) => photos.find((p) => p.id === id)).filter(Boolean);
  const confirm = async () => {
    setBusy(true);
    try { await api.post(`/client/galleries/${galleryId}/keep/confirm`, { photo_ids: ids, media_consent: consent }); toast.success("Vos photographies sont confirmees"); onConfirmed(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0B1120] border-[#1A2332] text-white w-[calc(100vw-1.5rem)] max-w-lg" data-testid="keep-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="text-left">Confirmer mes {ids.length} photographies</DialogTitle>
          <DialogDescription className="text-[#94A3B8] text-left">Elles deviendront les images permanentes de votre evenement. Apres confirmation, seule l'equipe GLOTECH pourra rouvrir la selection.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-5 gap-2">
          {chosen.map((p) => (
            <div key={p.id} className="relative aspect-square rounded-lg overflow-hidden bg-[#060B14]">
              <img src={mediaUrl(p.thumb_url)} alt="" className="w-full h-full object-cover" />
              <button onClick={() => onRemove(p.id)} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 flex items-center justify-center" aria-label="Retirer" data-testid={`keep-remove-${p.id}`}><X className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
        <div className="space-y-2">
          <p className="text-sm">Autorisez-vous GLOTECH EVENT a publier ces photographies dans sa rubrique Medias ?</p>
          {[[true, "J'autorise la publication", "GLOTECH verifiera les photos et les autorisations des personnes photographiees avant toute publication"],
            [false, "Je n'autorise pas la publication", "Vos photos restent conservees dans votre galerie privee"]].map(([v, t, d]) => (
            <button key={t} onClick={() => setConsent(v)} data-testid={`keep-consent-${v ? "yes" : "no"}`}
              className={`text-left w-full rounded-lg border px-3 py-2.5 transition-colors ${consent === v ? "border-[#0099CC] bg-[#0099CC]/10" : "border-[#1A2332] hover:border-[#334155]"}`}>
              <span className="block text-sm">{t}</span><span className="block text-xs text-[#64748B] mt-0.5">{d}</span>
            </button>
          ))}
        </div>
        <Button disabled={consent === null || busy || ids.length !== required} onClick={confirm} className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="keep-confirm-submit">Valider definitivement</Button>
      </DialogContent>
    </Dialog>
  );
}
